import { useState, useContext, useMemo, useEffect, useCallback } from "react";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/hooks/useI18n";
import { toast } from "@/hooks/useToast";
import { Inbox, Map, CheckCircle, Plus, XCircle } from "lucide-react";

import { TravelRequestModal, ClientRequestModal } from "@/lib/requests";
import { Modal } from "@/components/ui/modal";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { TabsHeader } from "@/components/ui/tabs-header";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { useSidebar } from "@/contexts/SidebarContext";
import { AuthContext } from "@/lib/auth/models/auth.model";
import { useDashboardTravelRequest, useDashboardTravelRequestCounts } from "@/hooks/useDashboardLists";
import { useTravelRequestActions } from "@/hooks/useTravelRequestActions";

import { DashboardRequestsTab } from "@/lib/dashboard/DashboardRequestsTab";
import { RequestsTabI18n } from "@/lib/dashboard/types";
import type { DashboardTravelRequestView } from "@/integrations/supabase/rpc-types";

/**
 * Le viste discendono dallo stato della richiesta (vedi dashboard_travel_request_view):
 * `completed` e `rejected` hanno ciascuna la propria scheda, tutto il resto è aperto. Da
 * prendere in carico o già in lavorazione stanno insieme: a distinguerle basta il
 * pallino sulla card.
 *
 * Le rifiutate stanno a parte dalle completate: una richiesta si rifiuta solo se non
 * è mai stata pagata, quindi di norma la scheda è vuota, e mescolarle alle completate
 * faceva sembrare chiuse con successo pratiche che non lo sono.
 */
const RequestsPage = () => {
  const { t } = useI18n();

  // La pagina legge solo i badge; ogni vista chiede da sola la propria pagina.
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshData = useCallback(() => setRefreshKey((k) => k + 1), []);
  const { data: counts } = useDashboardTravelRequestCounts(refreshKey);
  const { acceptTravelRequest, assignTravelRequest, closeTravelRequest } =
    useTravelRequestActions(refreshData);

  const authContext = useContext(AuthContext);
  if (!authContext) throw new Error("RequestsPage must be used inside an AuthProvider");
  const userRole = authContext.userRole;
  const currentUserId = authContext.user?.id;
  // Il cliente consulta e basta: niente nuova richiesta, niente azioni, niente schede
  // cliente/consulente (sarebbero la propria e quella di chi lo segue).
  const isTraveler = userRole === "traveler";

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSidebarCollapsed } = useSidebar();
  const activeTab = searchParams.get("tab") || "active";
  const selectedRequestId = searchParams.get("requestId");

  // ── Modal state ──────────────────────────────────────────────────────────
  const [selectedTravelRequest, setSelectedTravelRequest] = useState<DashboardTravelRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequestForItinerary, setSelectedRequestForItinerary] = useState<DashboardTravelRequest | null>(null);
  const [isCreateItineraryModalOpen, setIsCreateItineraryModalOpen] = useState(false);
  // "Nuova richiesta" sta nella testata: vale su tutte le viste, non solo sulle attive.
  const [isCreateRequestModalOpen, setIsCreateRequestModalOpen] = useState(false);

  // ── Modal handlers ───────────────────────────────────────────────────────
  const handleViewRequest = (request: DashboardTravelRequest) => {
    setSelectedTravelRequest(request);
    setIsModalOpen(true);
  };

  const handleCloseRequestModal = () => {
    setIsModalOpen(false);
    setSelectedTravelRequest(null);

    if (!selectedRequestId) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("requestId");
    setSearchParams(nextParams);
  };

  const handleCreateItinerary = (request: DashboardTravelRequest) => {
    setSelectedRequestForItinerary(request);
    setIsCreateItineraryModalOpen(true);
  };

  const handleStartBuilding = () => {
    navigate(
      selectedRequestForItinerary
        ? `/itinerary-builder?requestId=${selectedRequestForItinerary.id}`
        : "/itinerary-builder"
    );
  };

  const handleTabChange = (value: string) => setSearchParams({ tab: value });

  // ── Deep link a una travel request (?requestId=…) ─────────────────────────
  // Tipicamente si arriva da una notifica o da una mail: la richiesta può stare in
  // qualunque vista e pagina, quindi la si chiede per id invece di cercarla in lista.
  const { data: deepLink, loading: deepLinkLoading } = useDashboardTravelRequest(selectedRequestId);

  useEffect(() => {
    if (!selectedRequestId || deepLinkLoading || deepLink.resolvedId !== selectedRequestId) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    if (deepLink.request) {
      setSelectedTravelRequest(deepLink.request);
      setIsModalOpen(true);

      // Senza `tab` esplicito si atterra sulla vista che contiene davvero la
      // richiesta: aprire la modale su una lista che non la elenca confonde.
      if (!searchParams.get("tab")) {
        nextParams.set("tab", deepLink.request.dashboardView ?? "active");
        setSearchParams(nextParams, { replace: true });
      }
      return;
    }

    // Eliminata, oppure non visibile con i permessi correnti: meglio dirlo che non
    // aprire nulla.
    toast({
      title: String(t("dashboard.requestNotFoundTitle")),
      description: String(t("dashboard.requestNotFoundDesc")),
      variant: "destructive",
    });
    nextParams.delete("requestId");
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRequestId, deepLink, deepLinkLoading]);

  // ── Config delle viste ───────────────────────────────────────────────────
  const viewConfigs: Array<{
    value: DashboardTravelRequestView;
    labelKey: string;
    icon: typeof Inbox;
    i18n: RequestsTabI18n;
  }> = useMemo(
    () => [
      {
        value: "active",
        labelKey: "requests.tabActive",
        icon: Inbox,
        i18n: {
          emptyTitleKey: "requests.noActiveTitle",
          emptyDescKey: "requests.noActiveDesc",
        },
      },
      {
        value: "completed",
        labelKey: "requests.tabCompleted",
        icon: CheckCircle,
        i18n: {
          emptyTitleKey: "requests.noCompletedTitle",
          emptyDescKey: "requests.noCompletedDesc",
        },
      },
      {
        value: "rejected",
        labelKey: "requests.tabRejected",
        icon: XCircle,
        i18n: {
          emptyTitleKey: "requests.noRejectedTitle",
          emptyDescKey: "requests.noRejectedDesc",
        },
      },
    ],
    []
  );

  const tabs = useMemo(
    () =>
      viewConfigs.map((cfg) => ({
        value: cfg.value,
        label: t(cfg.labelKey as any),
        icon: cfg.icon,
        badge: (counts.byView[cfg.value] ?? 0).toString(),
      })),
    [viewConfigs, t, counts]
  );

  return (
    <PrivateLayout
      pageTitle={t("requests.pageTitle")}
      pageDescription={isTraveler ? t("requests.pageDescriptionTraveler") : t("requests.pageDescription")}
      showBackButton={false}
      // Altezza piena: la lista riempie lo spazio rimasto e la paginazione resta in fondo.
      contentClassName="h-full flex flex-col min-h-0 py-2 md:py-4"
      headerAction={
        isTraveler ? undefined : (
        <Button size="lg" className="h-8 px-3" onClick={() => setIsCreateRequestModalOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          {t("clientRequest.newRequest")}
        </Button>
        )
      }
      headerActionCompact={
        isTraveler ? undefined : (
        <Button
          size="sm"
          className="h-8 flex-shrink-0 whitespace-nowrap rounded-full px-2.5 text-xs"
          onClick={() => setIsCreateRequestModalOpen(true)}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          {t("clientRequest.newRequest")}
        </Button>
        )
      }
    >
      <div className="flex flex-1 flex-col min-h-0 gap-6">
        <TabsHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={tabs}
          sidebarCollapsed={isSidebarCollapsed}
        />

        <div className="flex flex-1 flex-col min-h-0 w-full xl:px-4 md:px-4 mx-auto">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-1 flex-col min-h-0 w-full">
            {/* Radix lascia nel DOM anche i pannelli inattivi, nascosti con l'attributo `hidden`:
                la classe `flex` lo scavalcherebbe e i pannelli vuoti si dividerebbero
                l'altezza con quello attivo: da qui data-[state=inactive]:hidden. */}
            {viewConfigs.map((cfg) => (
              <ProfileTabsContent
                key={cfg.value}
                value={cfg.value}
                className="flex flex-1 flex-col min-h-0 data-[state=inactive]:hidden"
              >
                <DashboardRequestsTab
                  view={cfg.value}
                  userRole={userRole}
                  refreshKey={refreshKey}
                  onRefresh={refreshData}
                  onViewRequest={handleViewRequest}
                  onCreateItinerary={handleCreateItinerary}
                  i18n={cfg.i18n}
                />
              </ProfileTabsContent>
            ))}
          </Tabs>
        </div>
      </div>

      {/* Nuova richiesta cliente: stessa modale usata dal tab in modifica */}
      <ClientRequestModal
        isOpen={!isTraveler && isCreateRequestModalOpen}
        onClose={() => setIsCreateRequestModalOpen(false)}
        onSuccess={refreshData}
      />

      {/* Travel request detail modal */}
      <TravelRequestModal
        isOpen={isModalOpen}
        onClose={handleCloseRequestModal}
        travelRequest={selectedTravelRequest}
        onAccept={async (id: string) => await acceptTravelRequest(id)}
        onDecline={async (id: string) => await closeTravelRequest(id)}
        onAssign={async (id: string, designerId: string) =>
          await assignTravelRequest(id, designerId)
        }
        onCloseRequest={
          isTraveler ? undefined : async (id: string) => await closeTravelRequest(id)
        }
        isAdmin={userRole === "admin"}
        agencyId={userRole === "agency" ? currentUserId : undefined}
        enableDetailSheets={!isTraveler}
        showActions={!isTraveler && selectedTravelRequest?.status === "pending"}
      />

      {/* Create itinerary confirmation modal */}
      <Modal
        isOpen={isCreateItineraryModalOpen}
        onClose={() => {
          setIsCreateItineraryModalOpen(false);
          setSelectedRequestForItinerary(null);
        }}
        title=""
        size="sm"
        showHeader={false}
        footer={
          <div className="flex items-center justify-between w-full">
            <Button
              variant="ghost"
              onClick={() => {
                setIsCreateItineraryModalOpen(false);
                setSelectedRequestForItinerary(null);
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button onClick={handleStartBuilding} className="flex items-center gap-2">
              {t("dashboard.startBuilding")}
            </Button>
          </div>
        }
      >
        <div className="text-center py-20">
          <div className="flex justify-center mb-4">
            <Map className="h-8 w-8" />
          </div>
          <h2 className="text-gray-900 mb-2">{t("dashboard.readyToCreate")}</h2>
          <p className="text-gray-600">{t("dashboard.eachItineraryIndependent")}</p>
        </div>
      </Modal>
    </PrivateLayout>
  );
};

export default RequestsPage;
